/**
 * Who is using the shared device, kept in sessionStorage.
 *
 * sessionStorage rather than memory so a reload keeps the name, and rather
 * than localStorage so closing the browser forgets it. Read by the API client
 * on every request (outside React), and by components through
 * {@link useKioskPerson}, which re-renders when it changes (1 Oct).
 */

import { useSyncExternalStore } from "react";

import { logger } from "@/lib/logger";

import { type KioskPerson, parseKioskPerson } from "./kiosk";

const STORAGE_KEY = "helpdesk.kioskPerson";

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch (error) {
    logger.debug("[kiosk] sessionStorage unavailable:", error);
    return null;
  }
}

/* Cached so `useSyncExternalStore` gets the same object until it changes. */
let current: KioskPerson | null = parseKioskPerson(storage()?.getItem(STORAGE_KEY) ?? null);
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The person at the device, or null when nobody has said yet. */
export function getKioskPerson(): KioskPerson | null {
  return current;
}

/** Remember who is at the device (already validated by the step). */
export function setKioskPerson(person: KioskPerson): void {
  current = { name: person.name.trim(), contact: person.contact.trim() };
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch (error) {
    // Still held in memory for this page; only a reload would forget it.
    logger.warn("[kiosk] could not store the person", error);
  }
  logger.debug("[kiosk] person set");
  emit();
}

/** Forget the person: "Not you?", sign-out, or the server asking again. */
export function clearKioskPerson(): void {
  if (current === null && storage()?.getItem(STORAGE_KEY) == null) {
    return;
  }
  current = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.debug("[kiosk] could not clear the stored person", error);
  }
  logger.debug("[kiosk] person cleared");
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The person at the device, re-rendering when it changes. */
export function useKioskPerson(): KioskPerson | null {
  return useSyncExternalStore(subscribe, getKioskPerson, getKioskPerson);
}
